// Local copy of the suite-navigation helpers of @orbitalwatch/design 0.3.0
// (src/components/nav-bar/orbitalWatchApps.ts in OrbitalWatch.Packages.Web.Design).
// This app still carries its own NavBar; delete this file when it adopts the package
// and import the same names from "@orbitalwatch/design" (work item #11413).

/** One entry of the app switcher. */
export interface INavBarAppMenuItem {
  label: string;
  href: string;
}

/**
 * Where the OrbitalWatch suite links point. There is no UAT; a laptop ("dev")
 * runs against the QA resources, so anything that is not "prod" uses QA hosts.
 */
export type OrbitalWatchEnvironment = "qa" | "prod";

/** The suite's apps, in app-switcher order. */
export type OrbitalWatchApp = "ark" | "aurora" | "vision" | "atlas" | "identity" | "pulsar";

const APP_LABELS: Record<OrbitalWatchApp, string> = {
  ark: "Ark",
  aurora: "Aurora",
  vision: "Vision",
  atlas: "Atlas",
  identity: "Identity",
  pulsar: "Pulsar",
};

/** Apps that only exist in production (no `.qa.` host). */
const PROD_ONLY: ReadonlySet<OrbitalWatchApp> = new Set<OrbitalWatchApp>(["pulsar"]);

/**
 * Normalises an environment name or a URL into `"qa" | "prod"`.
 *
 * - `"prod"` / `"production"` → `"prod"`; any other name (`"qa"`, `"dev"`, `"local"`) → `"qa"`.
 * - A URL (anything containing `://`) is classified by its host: a `.qa.` label,
 *   `localhost` or `127.0.0.1` → `"qa"`, otherwise `"prod"`. Apps pass their
 *   configured backend URL (`AppConfiguration.App.BackendUrl`), so a laptop running
 *   against prod APIs (`appconfiguration.local-prod.json`) links to prod too.
 * - Empty/undefined → `"prod"` (the safe default for a deployed build).
 */
export function orbitalWatchEnvironment(envOrUrl?: string | null): OrbitalWatchEnvironment {
  const value = (envOrUrl ?? "").trim().toLowerCase();
  if (!value) return "prod";
  if (value.includes("://")) {
    let host = "";
    try { host = new URL(value).hostname; } catch { host = value; }
    if (host === "localhost" || host === "127.0.0.1" || host.startsWith("qa.") || host.includes(".qa.")) return "qa";
    return "prod";
  }
  return value === "prod" || value === "production" ? "prod" : "qa";
}

/** Entry URL of an app, e.g. `https://identity.qa.orbitalwatch.dev/`. Prod-only apps always get their prod host. */
export function orbitalWatchAppUrl(app: OrbitalWatchApp, env?: string | null): string {
  const qa = orbitalWatchEnvironment(env) === "qa" && !PROD_ONLY.has(app);
  return `https://${app}${qa ? ".qa" : ""}.orbitalwatch.dev/`;
}

/** The signed-in user's own account page in Identity: `https://identity[.qa].orbitalwatch.dev/me/profile`. */
export function orbitalWatchAccountUrl(env?: string | null): string {
  return `${orbitalWatchAppUrl("identity", env)}me/profile`;
}

export interface IOrbitalWatchAppsOptions {
  /**
   * What to do with a prod-only app (Pulsar) outside prod. `"hint"` (default)
   * keeps it, linking to prod, labelled "Pulsar (prod)"; `"hide"` drops it.
   */
  prodOnlyOutsideProd?: "hint" | "hide";
  /** Apps to leave out, e.g. the current one. Default: none (the current app stays listed). */
  exclude?: OrbitalWatchApp[];
}

/**
 * Default app-switcher list for `NavBar` `appMenuItems`: Ark, Aurora, Vision,
 * Atlas, Identity, Pulsar with `https://<app>[.qa].orbitalwatch.dev/` hosts.
 * Pulsar is prod-only: outside prod it links to prod as "Pulsar (prod)" (or is
 * hidden with `prodOnlyOutsideProd: "hide"`). Append app-specific entries
 * (e.g. Azure DevOps) to the returned array.
 *
 * ```tsx
 * const env = orbitalWatchEnvironment(config.App.BackendUrl);
 * <NavBar appMenuItems={orbitalWatchApps(env)} accountUrl={orbitalWatchAccountUrl(env)} … />
 * ```
 */
export function orbitalWatchApps(env?: string | null, options: IOrbitalWatchAppsOptions = {}): INavBarAppMenuItem[] {
  const environment = orbitalWatchEnvironment(env);
  const exclude = new Set(options.exclude ?? []);
  const hint = (options.prodOnlyOutsideProd ?? "hint") === "hint";
  const apps = Object.keys(APP_LABELS) as OrbitalWatchApp[];
  const items: INavBarAppMenuItem[] = [];
  for (const app of apps) {
    if (exclude.has(app)) continue;
    const outsideProd = PROD_ONLY.has(app) && environment !== "prod";
    if (outsideProd && !hint) continue;
    items.push({
      label: outsideProd ? `${APP_LABELS[app]} (prod)` : APP_LABELS[app],
      href: orbitalWatchAppUrl(app, environment),
    });
  }
  return items;
}
